import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { authService } from '../services/authService';
import { loginSuccess } from '../store/slices/authSlice';

interface RegisterFormData {
  organizationName: string;
  organizationPhone: string;
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />;

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setLoading(true);

      const registerData = {
        organizationName: data.organizationName,
        organizationPhone: data.organizationPhone,
        fullName: data.fullName,
        email: data.email,
        password: data.password,
      };

      await authService.register(registerData);
      setRegisteredEmail(data.email);
      setRegistrationComplete(true);
    } catch (error: any) {
      const message = error.response?.data?.message;
      if (Array.isArray(message)) {
        message.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(message || t('auth.registerFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResending(true);
      await authService.resendVerification(registeredEmail);
      toast.success(t('auth.verificationEmailResent'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('auth.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  // Show success message after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-6 sm:p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.verifyYourEmail')}</h2>
          <p className="text-gray-600 mb-4">
            {t('auth.verificationEmailSentTo')}
          </p>
          <p className="font-medium text-blue-600 mb-6">{registeredEmail}</p>
          <p className="text-sm text-gray-500 mb-6">
            {t('auth.checkInboxAndSpam')}
          </p>
          <div className="space-y-3">
            <button
              onClick={handleResendVerification}
              disabled={resending}
              className="w-full py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 font-medium transition-colors"
            >
              {resending ? t('auth.sending') : t('auth.resendVerificationEmail')}
            </button>
            <Link
              to="/login"
              className="block w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors text-center"
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Transfer Paneli</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-2">{t('auth.createAccount')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.companyName')} *
            </label>
            <input
              type="text"
              {...register('organizationName', {
                required: t('auth.companyNameRequired'),
                minLength: { value: 2, message: t('auth.companyNameMinLength') },
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.companyPlaceholder')}
            />
            {errors.organizationName && (
              <p className="text-red-500 text-sm mt-1">{errors.organizationName.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.companyPhone')} *
            </label>
            <input
              type="tel"
              {...register('organizationPhone', {
                required: t('auth.phoneRequired'),
                minLength: { value: 10, message: t('auth.phoneMinLength') },
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.companyPhonePlaceholder')}
            />
            {errors.organizationPhone && (
              <p className="text-red-500 text-sm mt-1">{errors.organizationPhone.message}</p>
            )}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.fullName')} *
            </label>
            <input
              type="text"
              {...register('fullName', {
                required: t('auth.fullNameRequired'),
                minLength: { value: 2, message: t('auth.fullNameMinLength') },
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.fullNamePlaceholder')}
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.emailAddress')} *
            </label>
            <input
              type="email"
              {...register('email', {
                required: t('auth.emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('auth.emailInvalid'),
                },
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.enterEmail')}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password')} *
            </label>
            <input
              type="password"
              {...register('password', {
                required: t('auth.passwordRequired'),
                minLength: { value: 8, message: t('auth.passwordMinLength') },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: t('auth.passwordPattern'),
                },
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.minCharacters')}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('auth.passwordHint')}
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.confirmPassword')} *
            </label>
            <input
              type="password"
              {...register('confirmPassword', {
                required: t('auth.confirmPasswordRequired'),
                validate: (value) => value === password || t('auth.passwordMismatch'),
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.confirmYourPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors min-h-[44px]"
          >
            {loading ? t('auth.creating') : t('auth.createAccountBtn')}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">{t('auth.orDivider')}</span>
          </div>
        </div>

        {/* Google Register */}
        <div className="flex justify-center">
          {googleLoading ? (
            <div className="text-sm text-gray-500">{t('common.loading')}</div>
          ) : (
            <GoogleLogin
              onSuccess={async (credentialResponse: import('@react-oauth/google').CredentialResponse) => {
                if (!credentialResponse.credential) return;
                setGoogleLoading(true);
                try {
                  const response = await authService.googleAuth(credentialResponse.credential);
                  dispatch(loginSuccess(response));
                  toast.success(t('auth.loginSuccess'));
                  navigate('/app/dashboard');
                } catch (error: any) {
                  const message = error.response?.data?.message || t('auth.googleFailed');
                  toast.error(message);
                } finally {
                  setGoogleLoading(false);
                }
              }}
              onError={() => {
                toast.error(t('auth.googleFailed'));
              }}
              width="100%"
              text="signup_with"
              shape="rectangular"
              size="large"
            />
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-medium">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>

        {/* Terms */}
        <p className="mt-4 text-xs text-gray-500 text-center">
          {t('auth.termsText')}{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">{t('auth.termsOfService')}</Link>
          {' '}{t('auth.and')}{' '}
          <Link to="/privacy" className="text-blue-600 hover:underline">{t('auth.privacyPolicy')}</Link>
        </p>
      </div>
    </div>
  );
};
